import React, { useEffect } from "react";
import { motion } from 'framer-motion';
import { Gift, Users, Star, Award, Heart } from 'lucide-react';
import logo from '/img/logo.png';

export default function Scholarships({ 
  accent = "#115e32",
  pageTitle = "Scholarship and Financial Assistance | University of Creative Technology Chittagong",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  const items = [
    { title: 'Poor and meritorious students', icon: <Heart size={22} /> },
    { title: 'Special waiver for siblings, offspring of freedom fighter, tribal students and national-level players', icon: <Users size={22} /> },
    { title: 'GPA 5.00 in SSC and HSC', icon: <Star size={22} /> },
    { title: 'Waiver on excellent result in semester', icon: <Award size={22} /> },
    { title: 'Need based scholarship', icon: <Gift size={22} /> },
  ];

  return (
    <section className="min-h-screen flex items-center justify-center py-20 px-4 md:px-12 bg-gradient-to-b from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF]">
      <div className="w-full max-w-6xl bg-white/60 rounded-3xl shadow-2xl overflow-hidden">
        {/* Hero */}
        <div className="relative grid md:grid-cols-2 gap-6 items-center p-8 md:p-12 bg-gradient-to-r from-[#6BCCF2] to-teal-50">
          <div className="z-10">
            <img src={logo} alt="UCTC" className="w-24 mb-4" />
            <motion.h1 initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.7 }} className="text-3xl md:text-4xl font-extrabold text-[#014864] leading-tight">
              Scholarships & Financial Assistance
            </motion.h1>
            <motion.p initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.9 }} className="mt-4 text-gray-700 max-w-xl">
              UCTC is committed to supporting students facing financial difficulties and rewarding outstanding performance. Explore the categories and find how you can apply.
            </motion.p>

            <div className="mt-6 flex gap-4">
              <a href="#apply" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#014864] to-[#6BCCF2] text-white font-semibold shadow hover:scale-105 transition">Apply Now</a>
              <a href="#contact" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#f0dca8] text-[#7c5b0e] bg-white/60 hover:scale-105 transition">Contact Office</a>
            </div>
          </div>

          {/* SVG & Accent */}
          <div className="relative">
            <motion.svg viewBox="0 0 500 300" className="w-full h-full" initial={{ scale: 0.98 }} animate={{ scale: 1 }} transition={{ duration: 0.8 }}>
              <defs>
                <linearGradient id="goldL" x1="0" x2="1">
                  <stop offset="0%" stopColor="#fff3d1" />
                  <stop offset="100%" stopColor="#ffe9b0" />
                </linearGradient>
                <linearGradient id="goldS" x1="0" x2="1">
                  <stop offset="0%" stopColor="#b88f18" />
                  <stop offset="100%" stopColor="#7c5b0e" />
                </linearGradient>
                <filter id="soft" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="12" stdDeviation="20" floodColor="#d7b86b" floodOpacity="0.25" />
                </filter>
              </defs>

              <g transform="translate(20,10)">
                <motion.rect x="0" y="40" rx="18" width="260" height="200" fill="#D6F5FE" filter="url(#soft)" animate={{ y: [40, 36, 40] }} transition={{ duration: 4, repeat: Infinity }} />
                <motion.path d="M300 60 q40 30 80 0 q-40 30 -80 0" stroke="url(#goldS)" strokeWidth="6" fill="none" strokeLinecap="round" animate={{ pathLength: [0, 1, 0] }} transition={{ duration: 6, repeat: Infinity }} />

                <motion.circle cx="340" cy="200" r="12" fill="#f1d28a" animate={{ cy: [200, 188, 200] }} transition={{ duration: 3.6, repeat: Infinity }} />
                <motion.circle cx="420" cy="160" r="8" fill="#ffdca6" animate={{ cy: [160, 172, 160] }} transition={{ duration: 2.8, repeat: Infinity }} />

                <g transform="translate(40,80)">
                  <rect x="0" y="0" width="120" height="24" rx="6" fill="#F0FCFF" />
                  <rect x="0" y="34" width="120" height="14" rx="4" fill="#F0FCFF" />
                </g>

                <motion.g transform="translate(180,110)" animate={{ rotate: [0, 3, 0] }} transition={{ duration: 5, repeat: Infinity }}>
                  <rect x="0" y="0" width="160" height="120" rx="12" fill="#E6FAFE" stroke="#01364C" />
                  <circle cx="40" cy="40" r="18" fill="#C6F0FD" />
                  <path d="M90 70 h40 v10 h-40z" fill="#C6F0FD" />
                </motion.g>
              </g>
            </motion.svg>
          </div>
        </div>

        {/* Body: list of scholarships */}
        <div className="p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#E6FAFE]">
          {items.map((it, idx) => (
            <motion.article key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: idx * 0.08 }} className="flex gap-4 items-start bg-white rounded-xl p-4 shadow-sm hover:shadow-lg transition">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#fff1d0] to-[#ffe7b0] text-[#7c5b0e]">
                {it.icon}
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">{it.title}</h4>
                <p className="text-sm text-gray-600 mt-1">UCTC supports eligible students through various scholarships and waivers. Check eligibility and application deadlines with the admissions office.</p>
              </div>
            </motion.article>
          ))}

          {/* Quick facts card */}
          <motion.div initial={{ scale: 0.98, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="col-span-1 md:col-span-2 bg-gradient-to-r from-[#B7EAFC] to-[#F0FCFF] rounded-2xl p-6 shadow-md border-t-4 border-[#e8c57a]">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-lg font-bold text-[#7c5b0e]">How to Apply</h5>
                <p className="text-sm text-gray-700 mt-1">Submit a scholarship application form along with supporting documents to the Student Affairs / Finance Office.</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Office Hours</div>
                <div className="font-semibold text-gray-800">9:00 AM — 4:30 PM</div>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <a className="px-4 py-2 rounded-md bg-[#b88f18] text-white font-semibold shadow hover:brightness-105 transition" href="#form">Get Application</a>
              <a className="px-4 py-2 rounded-md border border-[#f0dca8] text-[#7c5b0e] bg-white/60 hover:bg-white transition" href="#contact">Contact Office</a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
